import { getToken } from '@helpscout/hsds-tokens/getToken'
import type { GetTokenInput } from '@helpscout/hsds-tokens/getToken'
import styled from 'styled-components'
import { css } from 'styled-components'

import { ButtonUI } from 'hsds/components/button/Button.styles'

export const AttentionUI = styled.span`
  --status-icon-size: 24px;
  --status-size: 6px;
  --status-shadow-width: 4px;
  --status-offset: calc(calc(var(--status-icon-size) - var(--status-size)) / 2);
  --hsds-icon-button-status-color: ${getToken('statusDot.color.light.slate')};
  --hsds-icon-button-status-background-color: var(
    --hsds-button-background-color
  );
  --hsds-icon-button-status-transform: translate(
    var(--status-offset),
    calc(var(--status-offset) * -1)
  );
  position: absolute;
  width: var(--status-size);
  border-radius: ${getToken('radius.circle')};
  aspect-ratio: 1;
  box-shadow: 0 0 0 var(--status-shadow-width)
    var(--hsds-icon-button-status-background-color);
  background-color: var(--hsds-icon-button-status-color);
  transform: var(--hsds-icon-button-status-transform);

  &.is-color-red {
    --hsds-icon-button-status-color: ${getToken('statusDot.color.dark.red')};
  }
  &.is-color-green {
    --hsds-icon-button-status-color: ${getToken('statusDot.color.dark.green')};
  }
  &.is-color-slate {
    --hsds-icon-button-status-color: ${getToken('statusDot.color.dark.slate')};
  }
  &.is-color-yellow {
    --hsds-icon-button-status-color: ${getToken('statusDot.color.dark.yellow')};
  }
  &.is-color-purple {
    --hsds-icon-button-status-color: ${getToken('statusDot.color.dark.purple')};
  }
`

export const SuffixIconUI = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const ButtonIconUI = styled(ButtonUI)`
  &&& {
    --hsds-button-border-radius: ${getToken('radius.circle')};
    aspect-ratio: 1;
  }

  ${makeButtonColorStyles('white')};
  ${makeButtonColorStyles('grey')};
  ${makeButtonColorStyles('green')};
  ${makeButtonColorStyles('purple')};
  ${makeButtonColorStyles('blue')};
  ${makeButtonColorStyles('charcoal')};
  ${makeButtonColorStyles('clay')};

  /* Hidden rather than removed, so the button keeps its size. */
  &.is-loading > .c-Icon {
    visibility: hidden;
  }

  &.is-size-xs,
  &.is-size-sm {
    .c-IconButton__Attention {
      --status-icon-size: 20px;
    }
  }

  &.is-disabled,
  &:disabled {
    &.is-color-white {
      --hsds-button-disabled-color: ${getToken(
        'buttonIcon.color.disabled.text'
      )};
      --hsds-button-disabled-main-color: ${getToken(
        'buttonIcon.color.disabled.main'
      )};
    }
  }

  &:has(.c-IconButton__Attention) {
    position: relative;

    &:hover,
    &.is-active,
    &:active {
      .c-IconButton__Attention {
        --hsds-icon-button-status-background-color: var(
          --hsds-button-hover-background-color
        );
      }
    }

    &.is-disabled,
    &[disabled] {
      &.is-style-filled .c-IconButton__Attention {
        border-color: ${getToken(
          'buttonIcon.color.disabled.border'
        )} !important;

        &:before {
          background: ${getToken('buttonIcon.color.disabled.text')} !important;
        }
      }
    }

    &.is-seamless .c-IconButton__Attention {
      border-color: transparent;
      background: transparent;
      right: 1px;
      top: 1px;

      &:before {
        display: none;
      }
    }

    &.is-size-sm .c-IconButton__Attention {
      --statusSize: 10px;
    }
  }
`

function makeButtonColorStyles(color: string) {
  // Color is a known set of values (white, grey, green, etc.) but TypeScript
  // can't narrow the template literal, so we assert the dynamic paths.
  const token = (path: string) => getToken(path as GetTokenInput)
  return css`
    &.is-color-${color} {
      --hsds-button-main-color: ${token(`buttonIcon.color.${color}.main`)};
      --hsds-button-background-color: ${token(
        `buttonIcon.color.${color}.main`
      )};
      --hsds-button-hover-background-color: ${token(
        `buttonIcon.color.${color}.hover`
      )};
      --hsds-button-border-color: ${token(`buttonIcon.color.${color}.main`)};
      --hsds-button-hover-border-color: ${token(
        `buttonIcon.color.${color}.hover`
      )};
      --hsds-button-color: ${token(`buttonIcon.color.${color}.text`)};
      --hsds-button-hover-color: ${token(
        `buttonIcon.color.${color}.textHover`
      )};
    }
  `
}
